<template>
  <div
    class="emalino-callout"
    :class="[
      `emalino-callout--${tone}`,
      `emalino-callout--${emphasis}`,
      `emalino-callout--${size}`,
      {
        'emalino-callout--dense': dense,
        'emalino-callout--center': align === 'center',
        'emalino-callout--dismissible': dismissible,
      },
    ]"
    :role="role"
    :aria-label="ariaLabel"
  >
    <IconButton
      v-if="dismissible"
      icon="close"
      :ariaLabel="closeAriaLabel"
      size="sm"
      variant="standard"
      class="emalino-callout__close"
      @click="$emit('dismiss')"
    />

    <div class="emalino-callout__main">
      <div
        v-if="hasMedia"
        class="emalino-callout__media"
        aria-hidden="true"
      >
        <slot name="media">
          <q-icon :name="icon ?? ''" class="emalino-callout__icon" />
        </slot>
      </div>

      <div class="emalino-callout__content">
        <span v-if="eyebrow" class="emalino-callout__eyebrow">{{ eyebrow }}</span>

        <component
          :is="headingTag"
          v-if="title"
          class="emalino-callout__title"
        >
          {{ title }}
        </component>

        <p v-if="hasBody" class="emalino-callout__body">
          <slot>{{ body }}</slot>
        </p>

        <div v-if="$slots.actions" class="emalino-callout__actions">
          <slot name="actions" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Callout — responsive info / notice / onboarding card.
 *
 * One leading icon (or custom media), an optional eyebrow + heading + body,
 * an optional actions row, and an optional close button. Used for coach
 * marks, first-run heroes, empty states, upgrade nudges, and any inline
 * notice surface.
 *
 * THE point of this component: it reflows on its CONTAINER width, not the
 * viewport. A `container-type: inline-size` root + `@container` query means
 * the card stacks vertically (icon on top, body full width, actions stacked
 * full-width) the moment its box is narrow — even when the viewport is wide.
 * That fixes the classic bug where a notice rendered inside a ~280px
 * navigation drawer or a narrow side panel kept its icon | text | actions
 * columns and crowded into an unreadable mess. Viewport media queries can't
 * see that; container queries can.
 *
 * Context-free per the shared-package contract: all copy and a11y labels
 * arrive as props/slots (callers localize), styling rides CSS variables, and
 * the only output is the `dismiss` event. No third-party imports — the close
 * button reuses the shared IconButton atom and the layout is pure CSS.
 */
import { computed, useSlots } from 'vue';
import IconButton from '../atoms/buttons/IconButton.vue';

// Same as Fab: 'Callout' is the design-system name for this component and
// collides with no HTML element.
// eslint-disable-next-line vue/multi-word-component-names
defineOptions({ name: 'Callout' });

type CalloutTone = 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';
type CalloutEmphasis = 'subtle' | 'solid';
type CalloutSize = 'md' | 'lg';

interface Props {
  /** Leading Material icon name. Omit (and skip the `media` slot) for no icon. */
  icon?: string | undefined;
  /** Small uppercase label above the title (e.g. a "Tip · 1 of 3" counter). */
  eyebrow?: string | undefined;
  /** Heading text. */
  title?: string | undefined;
  /** Body copy. Ignored when the default slot is used for richer content. */
  body?: string | undefined;
  /** Colour scheme. `brand`/`info` ride --q-primary; the rest ride the Quasar status tokens. */
  tone?: CalloutTone;
  /** `subtle` = tinted surface + hairline border (default). `solid` = filled (brand gradient hero). */
  emphasis?: CalloutEmphasis;
  /** `md` = standard notice (default). `lg` = hero (bigger icon, heading, padding). */
  size?: CalloutSize;
  /** Tighter padding + smaller icon chip for cramped contexts. No effect when `size` is `lg` (the lg modifier controls padding). */
  dense?: boolean;
  /** Render a close (×) button; clicking it emits `dismiss`. */
  dismissible?: boolean;
  /**
   * Accessible name for the close button. Pass a localized string whenever
   * `dismissible` is true; the English fallback only guarantees the button is
   * never left unlabeled.
   */
  closeAriaLabel?: string;
  /** Heading element for the title — keep the document outline sane. */
  headingLevel?: 2 | 3 | 4;
  /** Root ARIA role. `note` for hints, `status` for live nudges, `region`/`complementary` for sections. */
  role?: string;
  /** Root accessible name (recommended for `region`/`complementary`). */
  ariaLabel?: string | undefined;
  /** Vertical alignment of the icon against the content in the wide (row) layout. */
  align?: 'start' | 'center';
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  eyebrow: undefined,
  title: undefined,
  body: undefined,
  tone: 'info',
  emphasis: 'subtle',
  size: 'md',
  dense: false,
  dismissible: false,
  closeAriaLabel: 'Dismiss',
  headingLevel: 3,
  role: 'note',
  ariaLabel: undefined,
  align: 'start',
});

defineEmits<{ (e: 'dismiss'): void }>();

const slots = useSlots();

const headingTag = computed(() => `h${props.headingLevel}` as const);
const hasMedia = computed(() => !!props.icon || !!slots.media);
const hasBody = computed(() => !!props.body || !!slots.default);
</script>

<style scoped lang="scss">
// Stacking threshold. When the callout's own box is narrower than this, it
// switches to a single column (no side-by-side icon/text/action columns).
// Container queries cannot read a CSS custom property in the range condition,
// so this is a fixed literal — generous enough that a navigation drawer
// (~280px) and phone content widths stack, while a normal page column stays
// in the comfortable row layout.
$callout-stack: 26rem; // ~416px

.emalino-callout {
  // Establish the query container. Everything below reflows on THIS box's
  // inline size, not the viewport.
  container-type: inline-size;
  container-name: callout;

  position: relative;
  border-radius: var(--radius-md, 12px);
  padding: var(--callout-pad, 1rem 1.125rem);
  // Tone accent — every colour below is derived from this single var.
  --callout-accent: var(--q-primary);
  --callout-fg: var(--text-color, #1e1e1e);
  --callout-fg-muted: var(--text-color-secondary, #4b4f55);
}

// ---------------------------------------------------------------------------
//   Tone → accent colour
// ---------------------------------------------------------------------------
.emalino-callout--brand,
.emalino-callout--info {
  --callout-accent: var(--q-primary, #1a4d8f);
}
.emalino-callout--success {
  --callout-accent: var(--q-positive, #18794e);
}
.emalino-callout--warning {
  --callout-accent: var(--q-warning, #9a6700);
}
.emalino-callout--danger {
  --callout-accent: var(--q-negative, #b3261e);
}
.emalino-callout--neutral {
  --callout-accent: var(--text-color-muted, #686d73);
}

// ---------------------------------------------------------------------------
//   Emphasis — subtle (tinted) vs solid (filled)
// ---------------------------------------------------------------------------
.emalino-callout--subtle {
  background: color-mix(in oklab, var(--callout-accent) 8%, var(--card-bg, #ffffff));
  border: 1px solid color-mix(in oklab, var(--callout-accent) 20%, transparent);
}

.emalino-callout--solid {
  // On-fill foreground tokens. Dark-fill tones (brand/info/danger) carry white
  // text; light fills (warning amber, success green) would fail WCAG against
  // white, so they flip to dark text — the conventional amber/green-alert look.
  --on-solid: #ffffff;
  --on-solid-muted: rgba(255, 255, 255, 0.92);
  --on-solid-chip-bg: rgba(255, 255, 255, 0.16);
  --on-solid-chip-border: rgba(255, 255, 255, 0.22);

  --callout-fg: var(--on-solid);
  --callout-fg-muted: var(--on-solid-muted);
  color: var(--on-solid);
  border: 1px solid transparent;
  // Brand solid is the gradient hero; other tones fill flat with the accent.
  background: var(--callout-accent);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);

  &.emalino-callout--brand {
    background: var(--brand-gradient, linear-gradient(135deg, #1a4d8f 0%, #1f9da0 100%));
    box-shadow: 0 8px 24px rgba(26, 77, 143, 0.22);
  }

  &.emalino-callout--warning,
  &.emalino-callout--success {
    --on-solid: #1c1f23;
    --on-solid-muted: rgba(0, 0, 0, 0.7);
    --on-solid-chip-bg: rgba(0, 0, 0, 0.1);
    --on-solid-chip-border: rgba(0, 0, 0, 0.16);
  }
}

// ---------------------------------------------------------------------------
//   Layout — wide (row) is the default; @container flips it to a column.
// ---------------------------------------------------------------------------
.emalino-callout__main {
  display: flex;
  gap: var(--callout-gap, 0.875rem);
  align-items: flex-start;
}

.emalino-callout--center .emalino-callout__main {
  align-items: center;
}

.emalino-callout__media {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-sm, 8px);
  background: color-mix(in oklab, var(--callout-accent) 16%, transparent);
  color: var(--callout-accent);
}

.emalino-callout--solid .emalino-callout__media {
  background: var(--on-solid-chip-bg);
  border: 1px solid var(--on-solid-chip-border);
  color: var(--on-solid);
}

.emalino-callout__icon {
  font-size: 1.375rem;
}

.emalino-callout__content {
  flex: 1 1 auto;
  min-width: 0; // let long words wrap instead of stretching the flex item
}

.emalino-callout__eyebrow {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--callout-accent);
  margin-bottom: 0.2rem;
}
.emalino-callout--solid .emalino-callout__eyebrow {
  color: var(--callout-fg-muted);
}

.emalino-callout__title {
  margin: 0;
  font-size: 0.975rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.005em;
  color: var(--callout-fg);
}

.emalino-callout__body {
  margin: 0.2rem 0 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--callout-fg-muted);
}

.emalino-callout__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

// Close button — pinned to the top-right corner so it never occupies a
// layout column (and so it survives the row→column reflow unchanged).
// :deep() is required: IconButton sets `inheritAttrs: false` and forwards the
// fall-through `class` onto its INNER <button>, which does NOT receive this
// component's scoped-style id. A plain `.emalino-callout__close { … }` would
// compile to `.emalino-callout__close[data-v-…]` and match nothing, so the
// dismiss button would drop into normal flow instead of pinning to the corner.
.emalino-callout :deep(.emalino-callout__close) {
  position: absolute;
  top: 0.375rem;
  inset-inline-end: 0.375rem;
  color: var(--callout-fg-muted);
}
.emalino-callout--solid :deep(.emalino-callout__close) {
  color: var(--callout-fg-muted);
}
// Keep text/title clear of the corner button.
.emalino-callout--dismissible .emalino-callout__content {
  padding-inline-end: 1.75rem;
}

// ---------------------------------------------------------------------------
//   Dense
// ---------------------------------------------------------------------------
.emalino-callout--dense {
  --callout-pad: 0.75rem 0.875rem;
  --callout-gap: 0.75rem;

  .emalino-callout__media {
    width: 2rem;
    height: 2rem;
  }
  .emalino-callout__icon {
    font-size: 1.125rem;
  }
  .emalino-callout__actions {
    margin-top: 0.5rem;
  }
}

// ---------------------------------------------------------------------------
//   Large (hero) size
// ---------------------------------------------------------------------------
.emalino-callout--lg {
  --callout-pad: 1.5rem 1.75rem;
  --callout-gap: 1.125rem;
  border-radius: var(--radius-lg, 16px);

  .emalino-callout__media {
    width: 3.25rem;
    height: 3.25rem;
    border-radius: var(--radius-md, 12px);
  }
  .emalino-callout__icon {
    font-size: 1.875rem;
  }
  .emalino-callout__title {
    font-size: 1.25rem;
    font-weight: 700;
  }
  .emalino-callout__body {
    font-size: 0.95rem;
    margin-top: 0.35rem;
    max-width: 56ch;
  }
  .emalino-callout__actions {
    margin-top: 1rem;
  }
}

// ---------------------------------------------------------------------------
//   Narrow container → single column (the core of the component)
// ---------------------------------------------------------------------------
// Progressive enhancement: the row layout above is the universal default, so
// browsers without container-query support simply keep it (status quo). Where
// container queries are supported (Baseline since 2023), a narrow box stacks.
@supports (container-type: inline-size) {
  @container callout (max-width: #{$callout-stack}) {
    .emalino-callout__main {
      flex-direction: column;
      gap: 0.625rem;
    }

    // Icon centres with the heading in row mode but should hug the start when
    // it sits on its own row above the content.
    .emalino-callout--center .emalino-callout__main {
      align-items: flex-start;
    }

    // Stacked: actions become full-width, one per row — no cramped side-by-side
    // buttons, which is exactly the "ugly columns" failure mode in a drawer.
    .emalino-callout__actions {
      flex-direction: column;
      align-items: stretch;
    }
    .emalino-callout__actions > :deep(*) {
      width: 100%;
    }
  }
}
</style>
